import { useState, useMemo, useEffect, useCallback, RefObject } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import debounce from 'lodash/debounce';
import type { FilterState } from '@/utilities/types/shared-filter';

// Enable the required plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface UseVirtualizedTableProps {
  data: any[];
  filterState: FilterState;
  containerRef: RefObject<HTMLDivElement>;
  isProcessingTable?: boolean;
  customFilterFunction?: (data: any[], filterState: FilterState) => any[];
}

export const useVirtualizedTable = ({
  data,
  filterState,
  containerRef,
  customFilterFunction,
}: UseVirtualizedTableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Apply filtering logic
  const filteredRows = useMemo(() => {
    if (customFilterFunction) {
      return customFilterFunction(data, filterState);
    }

    return data.filter((row) => {
      // Regular filters
      const regularFiltersMatch = Object.entries(filterState.regularFilters || {}).every(
        ([key, values]) => {
          if (!values?.length) return true;
          const rowValue = String(row[key] || '').toLowerCase();
          return values.some((value) => String(value).toLowerCase().includes(rowValue));
        }
      );

      // Date ranges
      const dateRangesMatch = Object.entries(filterState.dateRanges || {}).every(
        ([key, range]) => {
          if (!range || (!range.start && !range.end)) return true;
          const rowDate = dayjs(row[key]);
          if (!rowDate.isValid()) return false;

          if (range.start && range.end) {
            return rowDate.isBetween(dayjs(range.start), dayjs(range.end), 'day', '[]');
          }
          if (range.start) {
            return rowDate.isSameOrAfter(dayjs(range.start), 'day');
          }
          if (range.end) {
            return rowDate.isSameOrBefore(dayjs(range.end), 'day');
          }
          return true;
        }
      );

      return regularFiltersMatch && dateRangesMatch;
    });
  }, [data, filterState, customFilterFunction]);

  const sortedData = useMemo(() => {
    if (!filterState.sort) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const { field, direction } = filterState.sort;

      // Special handling for ID field
      if (field === 'id') {
        const aNum = parseInt(String(a[field]), 10);
        const bNum = parseInt(String(b[field]), 10);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Regular string comparison for other fields
      const aVal = String(a[field] || '');
      const bVal = String(b[field] || '');
      return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [filteredRows, filterState.sort]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  useEffect(() => {
    if (page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const paginatedItems = useMemo(
    () => sortedData.slice(startIndex, endIndex),
    [sortedData, startIndex, endIndex]
  );

  const actualRowCount = useMemo(
    () => Math.min(sortedData.length - page * rowsPerPage, rowsPerPage),
    [sortedData.length, page, rowsPerPage]
  );

  const virtualizer = useVirtualizer({
    count: actualRowCount,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 52,
    overscan: 5,
  });

  useEffect(() => {
    const maxPage = Math.ceil(sortedData.length / rowsPerPage) - 1;
    if (page > maxPage) {
      setPage(0);
    }
    virtualizer.scrollToIndex(0);
  }, [sortedData.length, rowsPerPage, page, virtualizer]);

  const debouncedScroll = useMemo(
    () =>
      debounce(() => {
        virtualizer.measure();
      }, 16),
    [virtualizer]
  );

  const handleVirtualizedScroll = useCallback(() => {
    debouncedScroll();
  }, [debouncedScroll]);

  useEffect(() => {
    return () => {
      debouncedScroll.cancel();
    };
  }, [debouncedScroll]);

  const handlePageChange = useCallback(
    (_: unknown, newPage: number) => {
      const maxPage = Math.max(0, totalPages - 1);
      setPage(Math.min(newPage, maxPage));
      virtualizer.scrollToIndex(0);
    },
    [totalPages, virtualizer]
  );

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
      virtualizer.scrollToIndex(0);
    },
    [virtualizer]
  );

  const handleFirstPage = useCallback(() => {
    setPage(0);
  }, []);

  const handleLastPage = useCallback(() => {
    setPage(Math.max(0, Math.ceil(sortedData.length / rowsPerPage) - 1));
  }, [sortedData.length, rowsPerPage]);

  return {
    virtualRows: virtualizer.getVirtualItems(),
    totalSize: actualRowCount * 52,
    paginatedItems,
    filteredRows,
    page,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
    handleFirstPage,
    handleLastPage,
    virtualizer,
    sortedData,
  };
};
